import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, Skeleton, Stack, Typography } from '@mui/material';
import { Female as FemaleIcon, Male as MaleIcon, Wc as WcIcon } from '@mui/icons-material';
import LoadingSkeleton from './LoadingSkeleton';

const GenderSkeletonPlaceholder = () => (
  <Stack direction="row" spacing={2} mt={1.5} justifyContent="space-around" sx={{ width: '100%' }}>
    <Box textAlign="center" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
      <Skeleton variant="circular" width={32} height={32} animation="wave" sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width={50} height={28} animation="wave" />
      <Skeleton variant="text" width={60} height={16} animation="wave" sx={{ mt: 0.5 }} />
    </Box>
    <Box sx={{ borderLeft: '1px solid', borderColor: 'divider' }} />
    <Box textAlign="center" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
      <Skeleton variant="circular" width={32} height={32} animation="wave" sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width={50} height={28} animation="wave" />
      <Skeleton variant="text" width={60} height={16} animation="wave" sx={{ mt: 0.5 }} />
    </Box>
  </Stack>
);

const GenderCard = ({ genderSummary = { maleCount: 0, femaleCount: 0 }, visitorsInPeriod = 0, sx = {}, loading = false }) => {
  const { t } = useTranslation();

  const { maleCount = 0, femaleCount = 0 } = genderSummary || {};
  const hasGenderData = maleCount > 0 || femaleCount > 0;

  const genderTotal = maleCount + femaleCount;
  const maleRatio = genderTotal > 0 ? maleCount / genderTotal : 0;
  const femaleRatio = genderTotal > 0 ? femaleCount / genderTotal : 0;
  const malePercent = Math.round(maleRatio * 100);
  const femalePercent = Math.round(femaleRatio * 100);

  // Scale raw gender counts (bidirectional) to the confirmed entrance count so
  // the displayed numbers never exceed visitorsInPeriod.
  const displayMale = visitorsInPeriod > 0 ? Math.round(visitorsInPeriod * maleRatio) : maleCount;
  const displayFemale = visitorsInPeriod > 0 ? Math.round(visitorsInPeriod * femaleRatio) : femaleCount;

  return (
    <Card
      sx={{
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '14px',
        boxShadow: '0 1px 2px rgba(16,24,40,0.05)',
        ...sx,
      }}
    >
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <Box sx={{ color: 'text.secondary', display: 'flex', fontSize: 16 }}>
            <WcIcon fontSize="inherit" />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('dashboard:kpi.gender')}
          </Typography>
        </Box>

        <LoadingSkeleton loading={loading} height={105} customSkeleton={<GenderSkeletonPlaceholder />}>
          {!hasGenderData ? (
            <Box mt={1.5} textAlign="center" py={1}>
              <Typography variant="body2" color="text.secondary">
                {t('common:labels.noData')}
              </Typography>
            </Box>
          ) : (
            <Stack direction="row" spacing={2} mt={1.5} justifyContent="space-around">
              <Box textAlign="center">
                <MaleIcon sx={{ fontSize: 36, color: '#1B75BB' }} />
                <Typography variant="h3" fontWeight={700} color="#1B75BB">
                  {displayMale.toLocaleString('pt-PT')}
                </Typography>
                {malePercent > 0 && (
                  <Typography variant="caption" color="#1B75BB" fontWeight={600}>
                    {malePercent}%
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('dashboard:kpi.masculine')}
                </Typography>
              </Box>
              <Box sx={{ borderLeft: '1px solid', borderColor: 'divider' }} />
              <Box textAlign="center">
                <FemaleIcon sx={{ fontSize: 36, color: '#E05A8D' }} />
                <Typography variant="h3" fontWeight={700} color="#E05A8D">
                  {displayFemale.toLocaleString('pt-PT')}
                </Typography>
                {femalePercent > 0 && (
                  <Typography variant="caption" color="#E05A8D" fontWeight={600}>
                    {femalePercent}%
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('dashboard:kpi.feminine')}
                </Typography>
              </Box>
            </Stack>
          )}
        </LoadingSkeleton>
      </CardContent>
    </Card>
  );
};

GenderCard.propTypes = {
  genderSummary: PropTypes.shape({
    maleCount: PropTypes.number,
    femaleCount: PropTypes.number,
  }),
  visitorsInPeriod: PropTypes.number,
  sx: PropTypes.object,
  loading: PropTypes.bool,
};

export default GenderCard;
